import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { format } from 'date-fns';
import DeletePopup from './popup/delete';

interface MatchingTransaction {
    date: string;
    description: string;
    amount: number;
    isSource?: boolean;
    guid?: string;
}

interface PatternAnalysisData {
    simplifiedDescription?: string;
    interval?: string;
    medianDays?: number;
    matchedRange?: [number, number];
    matchingTransactions?: MatchingTransaction[];
    startDate?: string;
    usedTolerancePercent?: number;
    amount?: number;
    message?: string;
    cutoffWarning?: { shouldWarn: boolean; reason?: string } | null;
    suggestedCategoryId?: number;
    comparisonType?: string;
}

interface PatternAnalysisDisplayProps {
    open: boolean;
    analysisData: PatternAnalysisData | null;
    onClose: () => void;
    onContinue: (sourceTransactionGuid?: string) => void;
    transactionGuid?: string;
    categories?: any[];
    isDescriptionOnlyMatch?: boolean;
    onReanalyze?: (sourceTransactionGuid?: string) => void;
    onReanalyzeByAI?: (sourceTransactionGuid?: string) => void;
    showReanalyzeButton?: boolean;
    showReanalyzeByAIButton?: boolean;
}

const PatternAnalysisDisplay: React.FC<PatternAnalysisDisplayProps> = ({
    open,
    analysisData,
    onClose,
    onContinue,
    transactionGuid,
    categories,
    isDescriptionOnlyMatch,
    onReanalyze,
    onReanalyzeByAI,
    showReanalyzeButton,
    showReanalyzeByAIButton
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showCutoffConfirm, setShowCutoffConfirm] = React.useState(false);

    const getCategoryName = (categoryId?: number): string => {
        if (!categoryId || !categories) return 'N/A';
        
        const findCategory = (cats: any[]): any => {
            for (const cat of cats) {
                if (cat.id === categoryId) return cat;
                if (cat.children) {
                    const found = findCategory(cat.children);
                    if (found) return found;
                }
            }
            return null;
        };
        
        const found = findCategory(categories);
        return found ? found.name : 'N/A';
    };

    if (!analysisData) return null;

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch {
            return dateString;
        }
    };

    const formatAmount = (amount?: number) => {
        if (amount === null || amount === undefined) return 'N/A';
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(amount);
        } catch {
            return String(amount);
        }
    };

    const handleContinue = () => {
        if (analysisData?.cutoffWarning?.shouldWarn) {
            setShowCutoffConfirm(true);
            return;
        }
        onContinue(transactionGuid);
    };

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    fontWeight: 600,
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <span>Pattern Analysis Results</span>
                {
                    analysisData.message && <span style={{ color: '#fff', textTransform: 'uppercase', background: 'red', borderRadius: '4px', padding: '2px 6px', marginLeft: '10px' }}>Not a recurring provision</span>
                }
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {/* Summary Info */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ my: 1, fontWeight: 600 }}>
                        Analysis Summary
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', rowGap: 2 }}>
                        <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                Description
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {analysisData.simplifiedDescription || 'N/A'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                Interval
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {analysisData.interval || 'N/A'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                Median Days Between Transactions
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {analysisData.medianDays ?? 'N/A'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatAmount(analysisData.amount)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                Next Occurrence
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(analysisData.startDate)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                Matched Range (days)
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {analysisData.matchedRange
                                    ? `${analysisData.matchedRange[0]} - ${analysisData.matchedRange[1]}`
                                    : 'N/A'}
                            </Typography>
                        </Box>
                        {analysisData.suggestedCategoryId !== undefined && (
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                    Suggested Category
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {getCategoryName(analysisData.suggestedCategoryId)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                {analysisData.matchingTransactions?.some(tx => !tx.isSource) && 
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            Matching Transaction(s) <span style={{ color: 'var(--color-primary)' }}>{isDescriptionOnlyMatch ? '(Description Only)' : (analysisData.comparisonType === 'ai' ? '(AI Analysis)' : `(Full Analysis at ${analysisData.usedTolerancePercent ?? 0}% tolerance)`)}</span> 
                        </Typography>
                        {analysisData.matchingTransactions && analysisData.matchingTransactions.length > 0 ? (
                            <TableContainer
                                component={Paper}
                                sx={{
                                    borderRadius: 1,
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: 'var(--color-container-background)' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analysisData.matchingTransactions
                                            .filter(tx => !tx.isSource)
                                            .map((tx, idx) => (
                                            <TableRow
                                                key={idx}
                                                sx={{
                                                    '&:hover': { backgroundColor: 'var(--color-container-background)' },
                                                    '&:last-child td': { borderBottom: 0 }
                                                }}
                                            >
                                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                                    {formatDate(tx?.date)}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                                    {tx?.description || 'N/A'}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        color: (tx?.amount ?? 0) < 0 ? 'var(--color-negative-amount)' : '#388e3c'
                                                    }}
                                                >
                                                    {formatAmount(tx?.amount)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                                No matching transactions found
                            </Typography>
                        )}
                    </Box>
                }
                {analysisData.matchingTransactions?.some(tx => tx.isSource) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            Source Transaction(s)
                        </Typography>
                        <TableContainer
                            component={Paper}
                            sx={{
                                borderRadius: 1,
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                backgroundColor: '#eafceb'
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'var(--color-container-background)' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analysisData.matchingTransactions
                                        .filter(tx => tx.isSource)
                                        .map((tx, idx) => (
                                        <TableRow
                                            key={idx}
                                            sx={{
                                                backgroundColor: 'var(--color-page-background)',
                                            }}
                                        >
                                            <TableCell sx={{ fontSize: '0.875rem' }}>
                                                {formatDate(tx?.date)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.875rem' }}>
                                                {tx?.description || 'N/A'}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500,
                                                    color: (tx?.amount ?? 0) < 0 ? 'var(--color-negative-amount)' : '#388e3c'
                                                }}
                                            >
                                                {formatAmount(tx?.amount)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: '1px solid #e0e0e0',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="primary"
                >
                    Cancel
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {showReanalyzeButton && onReanalyze && (
                        <Button
                            onClick={() => onReanalyze(transactionGuid)}
                            variant="outlined"
                            sx={{
                                borderColor: 'var(--color-primary)',
                                color: 'var(--color-primary)',
                                '&:hover': {
                                    borderColor: 'var(--color-primary)',
                                    backgroundColor: 'rgba(0, 182, 155, 0.04)'
                                }
                            }}
                        >
                            Reanalyze by Description
                        </Button>
                    )}
                    {showReanalyzeByAIButton && onReanalyzeByAI && (
                        <Button
                            onClick={() => onReanalyzeByAI(transactionGuid)}
                            variant="outlined"
                            sx={{
                                borderColor: '#7C3AED',
                                color: '#7C3AED',
                                '&:hover': {
                                    borderColor: '#6D28D9',
                                    backgroundColor: 'rgba(124, 58, 237, 0.04)'
                                }
                            }}
                        >
                            Reanalyze by AI
                        </Button>
                    )}
                    <Button
                        onClick={handleContinue}
                        variant="contained"
                        color="primary"
                        sx={{ color: '#fff' }}
                    >
                        Continue
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
        <DeletePopup
            open={showCutoffConfirm}
            message="This recurring charge may have stopped"
            description={analysisData?.cutoffWarning?.reason || 'The pattern looks stale compared to the most recent activity on this account.'}
            confirmLabel="Provision Anyway"
            cancelLabel="Cancel"
            onCancel={() => setShowCutoffConfirm(false)}
            onConfirm={() => {
                setShowCutoffConfirm(false);
                onContinue(transactionGuid);
            }}
        />
        </>
    );
};

export default PatternAnalysisDisplay;
